import { earningsCard } from "../billing/earnings";
import { workedVisitsCard } from "../billing/worked-visits";
import type { PersonRecord } from "../../../server/core/people";
import { openViewer } from "../../ui/viewer";
import type { ScreenContext } from "../../shell/types";
import { messagesCard } from "../../ui/messages";
import { foldAllButtons, foldCards, openCard } from "../../ui/fold";
import { activityPanel } from "../../ui/activity";
import { autosaveStatus, createAutosave } from "../../ui/autosave";
import { api, el, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { openModal, type Modal } from "../../ui/modal";
import { card, chip, ib, muted, table } from "../../ui/record";
import { sendButton } from "../../ui/send";
import { readingState } from "../../ui/textbox";
import { uploadControl, type UploadedFile } from "../../ui/upload";
import { PAUSED, READABLE_TYPES } from "../../../server/rules/ai";
import { area, field, pills, refuse, select } from "../clients/forms";
import { availabilityCard, matchingCard } from "./availability";
import { ptoCard } from "./pto";
import { payPackageCard } from "../billing/pay";
import { openOf, readyChip, type PeopleScreen } from "./people";

// The person record (brief 1B-2, mockup workerRec): who they are and what they do, their Board
// title and license, every document the title and module ask for (upload, approve, reject with a
// reason, waive with a reason, expiry), whether they're ready today and what stops them, which
// clients they qualify for, and the activity panel. Detail edits save on their own a moment
// after the last change; reviews save with their button. A caregiver's record also has the
// Availability card and their upcoming visits (brief 1D-1, app/screens/people/availability.ts).

type View = PersonRecord;
interface Band {
  name: string;
  initials: string;
  title: string;
  tiles: { key: string; label: string; value: string; level: string; card: string }[];
}
type Res = { ok: boolean; status: number; body: View & { error?: string } };
type Doc = View["documents"][number];

const EYEBROW: Record<PeopleScreen, string> = { caregivers: "Caregiver", drivers: "Driver" };
const usDate = (d: string | null) =>
  d ? `${d.slice(5, 7)}/${d.slice(8, 10)}/${d.slice(0, 4)}` : "–";

export async function personRecord(
  host: HTMLElement,
  ctx: ScreenContext,
  screen: PeopleScreen,
  id: string,
) {
  const page = el("div", { class: "record" });
  const activity = activityPanel("person", id, { autosaved: true });
  // Brief 1E-32: every card on the record folds (its messages too); on a caregiver, Ready to work
  // starts open and everything else starts folded.
  const wrap = el("div", { class: "recwrap" });
  wrap.append(page);
  foldCards(
    wrap,
    screen === "caregivers" ? "caregiver" : "driver",
    screen === "caregivers" ? { openFirst: ["Ready to work"] } : {},
  );
  host.append(wrap);
  let view: View;

  const first = (await api<View>("GET", `/api/people/${encodeURIComponent(id)}`)) as Res;
  if (!first.ok) {
    page.append(
      el(
        "div",
        { class: "blank", role: "alert" },
        icon("circle-slash", "big"),
        el("h2", {}, "Not available"),
        el(
          "p",
          { class: "muted" },
          "This person isn't here, or doesn't work in a module that's on.",
        ),
      ),
    );
    return;
  }
  view = first.body;
  host.append(activity.el);
  // Availability and upcoming visits (brief 1D-1): Home Care's caregivers; drawn once, kept.
  const availSlot = el("div");
  if (screen === "caregivers") void availabilityCard(availSlot, id);
  // The caregiver's side of matching (brief 1D-2): languages, OK with pets and the rest.
  const matchSlot = el("div");
  if (screen === "caregivers") void matchingCard(matchSlot, id);
  // The pay package (brief 1E-1): with the Money switch only.
  const paySlot = el("div", { class: "ws" });
  if (screen === "caregivers") void payPackageCard(paySlot, id);
  // Brief 1E-11: their balances and their week.
  const ptoSlot = el("div");
  void ptoCard(ptoSlot, id);
  // Brief 1E-33: Earnings by pay period, and her worked visits (Home Care's caregivers).
  const earnSlot = el("div");
  if (screen === "caregivers") void earningsCard(earnSlot, id);
  const workedSlot = screen === "caregivers" ? workedVisitsCard(ctx, "caregiver", id) : el("div");
  // Text, email, call or note them from here; replies land here (brief 1B-5).
  page.after(messagesCard("person", id, () => void activity.refresh()));

  /** Takes a saved answer: redraws the record and the activity. */
  async function took(res: Res): Promise<Res> {
    if (res.ok) {
      view = res.body;
      if (page.isConnected) draw();
      await activity.refresh();
    }
    return res;
  }
  const send = async (method: string, path: string, body?: unknown) =>
    took((await api<View>(method, `/api/people/${id}${path}`, body)) as Res);

  /** An autosaving modal: saves with `put` a moment after each change. */
  function autosaving(m: Modal, inputs: HTMLElement[], put: () => Promise<Res>) {
    const status = autosaveStatus();
    m.root.querySelector(".cardhead")?.after(el("div", { style: "margin:-4px 0 4px" }, status.el));
    const saver = createAutosave({
      save: async () => {
        const res = await put();
        if (res.ok) m.note.hidden = true;
        else if (res.status === 400 || res.status === 409)
          refuse(m.note, res.body.error, openOf(res.body));
        return res.ok ? { ok: true } : { ok: false, error: res.body.error };
      },
      onStatus: status.show,
    });
    for (const i of inputs) {
      i.addEventListener("input", () => saver.changed());
      i.addEventListener("change", () => saver.changed());
    }
  }

  // ---- Details, status, title and license -------------------------------------------------------
  function editDetails() {
    const p = view.person;
    const firstName = field("First name", p.firstName, { maxlength: "60" });
    const lastName = field("Last name", p.lastName, { maxlength: "60" });
    const mobile = field("Mobile", p.mobile, { type: "tel", maxlength: "20" });
    const email = field("Email", p.email, { type: "email", maxlength: "160" });
    const roles = pills(
      "Does",
      "checkbox",
      [
        ["caregiver", "Caregiver"],
        ["driver", "Driver"],
      ],
      p.roles,
      "Someone who both gives care and drives is one record in both lists.",
    );
    // The job they start with (brief 1E-7): their kind's job when added, changeable here.
    const job = select(
      "Job",
      [["", "No job yet"], ...view.jobs.map((j) => [j.id, j.name] as const)],
      view.job,
    );
    const a = p.address;
    const line1 = field("Street", a.addressLine1, { maxlength: "120" });
    const line2 = field("Apartment, unit", a.addressLine2, { maxlength: "120" });
    const city = field("City", a.city, { maxlength: "120" });
    const state = field("State", a.state, { maxlength: "2" });
    const zip = field("ZIP", a.zip, { maxlength: "10", inputmode: "numeric" });
    const m = openModal({
      title: `Edit ${p.name}`,
      sub: "Changes save as they're made.",
      body: [
        el(
          "div",
          { class: "form", style: "max-width:none" },
          el(
            "div",
            { class: "formgrid" },
            firstName.el,
            lastName.el,
            mobile.el,
            email.el,
            roles.el,
            job.el,
            el("div", { class: "wide eyebrow", style: "margin-top:4px" }, "Address"),
            el("div", { class: "wide" }, line1.el),
            line2.el,
            city.el,
            state.el,
            zip.el,
          ),
        ),
      ],
      wide: true,
    });
    firstName.input.focus();
    autosaving(
      m,
      [
        firstName.input,
        lastName.input,
        mobile.input,
        email.input,
        ...roles.inputs,
        job.input,
        line1.input,
        line2.input,
        city.input,
        state.input,
        zip.input,
      ],
      () =>
        send("PUT", "", {
          firstName: firstName.input.value,
          lastName: lastName.input.value,
          mobile: mobile.input.value || null,
          email: email.input.value || null,
          jobPresetId: job.input.value || null,
          roles: [
            ...roles.values(),
            ...view.person.roles.filter((r) => r !== "caregiver" && r !== "driver"),
          ],
          addressLine1: line1.input.value,
          addressLine2: line2.input.value,
          city: city.input.value,
          state: state.input.value,
          zip: zip.input.value,
        }),
    );
  }

  function changeStatus() {
    const p = view.person;
    const status = pills(
      "Status",
      "radio",
      [
        ["active", "Active"],
        ["inactive", "Inactive"],
        ["do_not_use", "Do not use"],
      ],
      [p.status],
      "Inactive or do not use isn't ready to work, whatever is on file.",
    );
    const reason = area("Reason", p.statusReason);
    const save = el("button", { type: "button", class: "btn pri" }, "Save status");
    const m = openModal({
      title: `Status of ${p.name}`,
      sub: "Inactive and do not use need a reason.",
      body: [el("div", { class: "form", style: "max-width:none" }, status.el, reason.el)],
      actions: [save],
    });
    save.addEventListener("click", async () => {
      save.disabled = true;
      const res = await send("POST", "/status", {
        status: status.values()[0],
        reason: reason.input.value,
      });
      save.disabled = false;
      if (!res.ok) return refuse(m.note, res.body.error);
      m.close();
      toast(`Status: ${res.body.person.statusLabel}`);
    });
  }

  function editTitle() {
    const pr = view.profile;
    const title = select(
      "Title",
      [["", "No title"], ...view.titles.map((t) => [t.code, `${t.code} · ${t.name}`] as const)],
      pr.title,
    );
    const state = field("License state", pr.licenseState, { maxlength: "2" });
    const number = field("License or certificate number", pr.licenseNumber, { maxlength: "30" });
    const specialties = field("Specialties", pr.specialties.join(", "), { maxlength: "300" });
    const languages = field("Languages", pr.languages.join(", "), { maxlength: "300" });
    const m = openModal({
      title: "Title and license",
      sub: "Maryland Board of Nursing titles only. Changes save as they're made.",
      body: [
        el(
          "div",
          { class: "form", style: "max-width:none" },
          el(
            "div",
            { class: "formgrid" },
            title.el,
            state.el,
            el("div", { class: "wide" }, number.el),
            specialties.el,
            languages.el,
            el(
              "p",
              { class: "hint wide", style: "margin:-4px 0 0" },
              "Separate specialties and languages with commas.",
            ),
          ),
        ),
      ],
      wide: true,
    });
    title.input.focus();
    autosaving(
      m,
      [title.input, state.input, number.input, specialties.input, languages.input],
      () =>
        send("PUT", "/profile", {
          title: title.input.value || null,
          licenseState: state.input.value || null,
          licenseNumber: number.input.value || null,
          specialties: specialties.input.value,
          languages: languages.input.value,
        }),
    );
  }

  // ---- A document: upload, review ---------------------------------------------------------------
  /** Puts an uploaded file on the document for review. */
  async function submitFile(d: Doc, fileId: string) {
    const res = await send("POST", `/documents/${d.code}/submit`, { fileId });
    if (!res.ok) toast("That wasn't added", res.body.error);
    else toast(`${d.name} uploaded`, "Needs review");
  }

  /** Whether AI reads this upload (brief 1B-8): reading on, and a photo, scan or PDF. */
  const readable = (f: UploadedFile) =>
    readingState() !== "off" && readingState() !== "not_set_up" && !!READABLE_TYPES[f.type];

  function review(d: Doc, justUploaded?: UploadedFile) {
    const reviewable = !d.restricted || view.seeResults;
    const number = field("Number", d.number, { maxlength: "60" });
    const issued = field("Issued", d.issuedOn, { type: "date" });
    const expires = field("Expires", d.expiresOn, { type: "date" });
    // Reading uploads (brief 1B-8): AI fills the fields the requirement asks for, each marked
    // "Read by AI, check it" until a person saves; saving says which ones were checked.
    const aiFilled = new Set<"number" | "issued" | "expires">();
    const aiNote = el("div", { class: "note", role: "status", hidden: true });
    const say = (...parts: (Node | string | null)[]) => {
      aiNote.hidden = false;
      aiNote.replaceChildren(...parts.filter((p): p is Node | string => p !== null));
    };
    const mark = (f: { el: HTMLElement }, key: "number" | "issued" | "expires") => {
      f.el.classList.add("airead");
      if (!f.el.querySelector(".aiflag"))
        f.el.append(
          el("span", { class: "aiflag" }, icon("sparkles-fill"), " Read by AI, check it"),
        );
      aiFilled.add(key);
    };
    async function readIt(f: UploadedFile) {
      if (!readable(f)) return;
      if (readingState() === "paused") return say(`${PAUSED}. Type the details in.`);
      say(icon("sparkles-fill"), " AI is reading it…");
      const res = await api<{
        fields: { number?: string; issuedOn?: string; expiresOn?: string };
        read: string[];
        nameCheck: string | null;
        nameMismatch: boolean;
        error?: string;
      }>("POST", "/api/ai/read", {
        person: id,
        requirement: d.code,
        file: f.id,
        module: ctx.section.id,
      });
      if (!res.ok) return say(res.body.error ?? "AI couldn't read it. Type the details in.");
      const got = res.body.fields;
      if (got.number) {
        number.input.value = got.number;
        mark(number, "number");
      }
      if (got.issuedOn) {
        issued.input.value = got.issuedOn;
        mark(issued, "issued");
      }
      if (got.expiresOn) {
        expires.input.value = got.expiresOn;
        mark(expires, "expires");
      }
      const filled = res.body.read.filter((k) => k !== "name").length;
      say(
        el(
          "p",
          {},
          icon("sparkles-fill"),
          filled
            ? ` AI filled ${filled} box${filled === 1 ? "" : "es"}. Check each one before saving.`
            : " AI couldn't read the details. Type them in.",
        ),
        res.body.nameCheck
          ? el(
              "p",
              { class: res.body.nameMismatch ? "bad-t" : "ok-t" },
              icon(res.body.nameMismatch ? "alert" : "check-circle"),
              ` ${res.body.nameCheck}`,
            )
          : null,
      );
    }
    const result =
      d.restricted && view.seeResults
        ? select(
            "Result",
            [
              ["", "–"],
              ["clear", "Clear"],
              ["not_clear", "Not clear"],
            ],
            d.result === "Clear" ? "clear" : d.result === "Not clear" ? "not_clear" : "",
          )
        : null;
    const reason = area("Reason (to reject or waive)", null);
    const file = el("div", { class: "field wide" });
    const drawFile = () => {
      const now = [...view.documents, ...view.other].find((x) => x.code === d.code) ?? d;
      const f = now.file;
      const getIt =
        f && f.id
          ? el("button", { type: "button", class: "btn sm" }, icon("eye"), " ", f.name)
          : null;
      getIt?.addEventListener(
        "click",
        () => f?.id && void openViewer({ file: { id: f.id, name: f.name } }),
      );
      const up = uploadControl({
        kind: "person",
        id,
        label: f ? "Upload a new file" : "Upload",
        camera: true,
        onUploaded: async (u) => {
          await submitFile(d, u.id);
          drawFile();
          said.textContent = "Uploaded. Review it, then approve or reject.";
          await readIt(u);
        },
      });
      file.replaceChildren(
        el("span", { class: "label" }, "File"),
        el(
          "div",
          { class: "row" },
          getIt ?? muted(f ? f.name : "Nothing uploaded yet"),
          f?.id ? sendButton({ files: [{ id: f.id, name: f.name }], document: d.name }) : null,
        ),
        view.canEdit ? up.el : el("span"),
      );
    };
    drawFile();
    const said = el(
      "p",
      { class: "small", role: "status" },
      `${d.chip.text}${d.rejectReason ? ` · ${d.rejectReason}` : ""}${d.waiverReason ? ` · ${d.waiverReason}` : ""}`,
    );
    const approve = el("button", { type: "button", class: "btn pri" }, icon("check"), " Approve");
    const reject = el("button", { type: "button", class: "btn" }, "Reject");
    const waive = el("button", { type: "button", class: "btn ghost" }, "Waive");
    const lines = [d.kindLabel, d.checkedAgainst, d.variants].filter(Boolean).join(" · ");
    const m = openModal({
      title: d.name,
      sub: lines,
      body: [
        el(
          "div",
          { class: "form", style: "max-width:none" },
          said,
          aiNote,
          el(
            "div",
            { class: "formgrid" },
            file,
            number.el,
            result ? result.el : el("div"),
            issued.el,
            expires.el,
            reason.el,
          ),
          d.restricted && !view.seeResults
            ? el(
                "p",
                { class: "note" },
                "Only people who can see background and drug results review this one. Its status shows here.",
              )
            : null,
        ),
      ],
      actions: view.canEdit && reviewable ? [approve, reject, waive] : [],
      wide: true,
    });
    if (justUploaded) void readIt(justUploaded);
    const act = async (b: HTMLButtonElement, path: string, payload: unknown, done: string) => {
      b.disabled = true;
      const res = await send("POST", `/documents/${d.code}/${path}`, payload);
      b.disabled = false;
      if (!res.ok) return refuse(m.note, res.body.error);
      m.close();
      toast(done);
    };
    approve.addEventListener(
      "click",
      () =>
        void act(
          approve,
          "approve",
          {
            number: number.input.value || null,
            issuedOn: issued.input.value || null,
            expiresOn: expires.input.value || null,
            ...(result ? { result: result.input.value || null } : {}),
            ...(aiFilled.size ? { aiChecked: [...aiFilled] } : {}),
          },
          `${d.name} approved`,
        ),
    );
    reject.addEventListener(
      "click",
      () => void act(reject, "reject", { reason: reason.input.value }, `${d.name} rejected`),
    );
    waive.addEventListener(
      "click",
      () => void act(waive, "waive", { reason: reason.input.value }, `${d.name} waived`),
    );
  }

  // ---- The band (brief 1E-32, mockup v90) ------------------------------------------------------------
  const bandSlot = el("div");
  let bandAsked = 0;
  async function drawBand() {
    const n = ++bandAsked;
    const res = await api<Band>("GET", `/api/people/${encodeURIComponent(id)}/band`);
    if (n !== bandAsked || !res.ok) return;
    const b = res.body;
    const tile = (t: Band["tiles"][number]) => {
      const btn = el(
        "button",
        { type: "button", class: "bt", "data-tile": t.key, "aria-label": `${t.label}: ${t.value}` },
        el("span", { class: "k" }, t.label),
        el("span", { class: "v" }, t.level === "plain" ? t.value : chip(t.value, t.level)),
      );
      btn.addEventListener("click", () => openCard(wrap, t.card));
      return btn;
    };
    bandSlot.replaceChildren(
      el(
        "section",
        { class: "card band keep", "aria-label": "Summary" },
        el(
          "div",
          { class: "bandtop" },
          el("span", { class: "pav big", "aria-hidden": "true" }, b.initials),
          el(
            "div",
            {},
            el("h2", { style: "margin:0" }, b.name),
            el("span", { class: "muted" }, b.title),
          ),
          el("span", { class: "actions", style: "margin-left:auto" }, ...foldAllButtons(wrap)),
        ),
        el("div", { class: "bgrid" }, ...b.tiles.map(tile)),
      ),
    );
  }

  // ---- Drawing ---------------------------------------------------------------------------------------
  function docTable(rows: readonly Doc[], label: string) {
    return table<Doc>(
      [
        {
          h: "Level",
          f: (x) =>
            x.level === "blocking"
              ? el("span", { class: "lvl" }, "Blocking")
              : x.level
                ? muted("Not blocking")
                : muted("–"),
        },
        {
          h: "Requirement",
          f: (x) =>
            el(
              "span",
              {},
              el("b", {}, x.name),
              x.variants ? el("div", { class: "muted small" }, x.variants) : null,
              x.notYet ? el("div", { class: "muted small" }, "Asked after hire") : null,
            ),
        },
        {
          h: "File",
          f: (x) => {
            const f = x.file;
            if (!f) return el("span", { class: "dot" }, "–");
            if (!f.id) return muted(f.name);
            const b = el("button", { type: "button", class: "btn sm ghost" }, f.name);
            b.addEventListener(
              "click",
              () => f.id && void openViewer({ file: { id: f.id, name: f.name } }),
            );
            return el(
              "span",
              { class: "filerow" },
              b,
              sendButton({ files: [{ id: f.id, name: f.name }], document: x.name }),
            );
          },
        },
        { h: "Uploaded", f: (x) => usDate(x.uploadedOn) },
        { h: "Expires", f: (x) => usDate(x.expiresOn) },
        {
          h: "Status",
          f: (x) =>
            el(
              "span",
              {},
              chip(x.chip.text, x.chip.level),
              x.result ? el("div", { class: "small" }, `Result: ${x.result}`) : null,
            ),
        },
        {
          h: "Notes",
          f: (x) => el("span", { class: "small" }, x.rejectReason ?? x.waiverReason ?? ""),
        },
        {
          h: "",
          f: (x) => {
            const acts = el("div", { class: "actions" });
            const canReview = !x.restricted || view.seeResults;
            if (view.canEdit && !x.file) {
              const up = uploadControl({
                kind: "person",
                id,
                label: "Upload",
                onUploaded: async (u) => {
                  await submitFile(x, u.id);
                  // AI reads it (brief 1B-8): the review opens with the fields filled to check.
                  if (readable(u) && view.canEdit && canReview)
                    review(
                      [...view.documents, ...view.other].find((y) => y.code === x.code) ?? x,
                      u,
                    );
                },
              });
              acts.append(up.el);
            }
            if (view.canEdit && canReview) {
              const open = el(
                "button",
                {
                  type: "button",
                  class: `btn sm${x.status === "submitted" ? " pri" : ""}`,
                  "aria-label": `${x.status === "submitted" ? "Review" : "Open"} ${x.name}`,
                },
                x.status === "submitted" ? "Review" : "Open",
              );
              open.addEventListener("click", () => review(x));
              acts.append(open);
            }
            return acts;
          },
        },
      ],
      rows,
      undefined,
      label,
    );
  }

  function draw() {
    const p = view.person;
    const pr = view.profile;
    const r = view.readiness;
    ctx.crumb(p.name);

    const headActions: Node[] = [];
    if (view.canEdit) {
      const e = el("button", { type: "button", class: "btn" }, icon("pencil"), " Edit");
      e.addEventListener("click", editDetails);
      const s = el("button", { type: "button", class: "btn" }, "Status");
      s.addEventListener("click", changeStatus);
      headActions.push(e, s);
    }
    const sub = [pr.title ?? "No title", ...p.roleLabels, p.statusLabel, p.mobile ?? "No mobile"]
      .filter(Boolean)
      .join(" · ");
    const head = el(
      "div",
      { class: "head" },
      el(
        "div",
        { class: "t" },
        el("div", { class: "eyebrow" }, EYEBROW[screen]),
        el("h1", {}, icon("person", "h1i"), p.name),
        el("div", { class: "muted" }, sub),
      ),
      el(
        "div",
        { class: "actions" },
        ...headActions,
        ...(screen === "caregivers" ? [] : foldAllButtons(wrap)),
      ),
    );
    if (screen === "caregivers") void drawBand();

    // The four boxes over a driver's record (mockup workerRec grid4); a caregiver has the band.
    const box = (label: string, value: Node | string) =>
      el(
        "div",
        { class: "card" },
        el("div", { class: "muted small" }, label),
        el("div", { style: "font-weight:700" }, value),
      );
    const boxes = el(
      "div",
      { class: "grid4" },
      box("Readiness", readyChip(r.ready)),
      box("Compliance", view.compliance),
      box(
        "Next expiry",
        view.nextExpiry
          ? el(
              "span",
              {},
              `${view.nextExpiry.name} `,
              chip(view.nextExpiry.text, view.nextExpiry.level),
            )
          : "–",
      ),
      box("Exclusion check", r.exclusionLabel),
    );

    // Readiness: ready, or every item that stops them, in plain English.
    const readiness = el(
      "section",
      { class: "card", "aria-label": "Ready to work" },
      el(
        "div",
        { class: "cardhead" },
        el(
          "div",
          {},
          el("h2", {}, r.ready ? "Ready to work" : "Not ready to work"),
          el(
            "span",
            { class: "small muted" },
            "Checked against the title's and the module's requirements.",
          ),
        ),
        readyChip(r.ready),
      ),
      r.missing.length
        ? el(
            "ul",
            { class: "plainlist", "aria-label": "What's missing" },
            ...r.missing.map((m) =>
              el(
                "li",
                {},
                chip(
                  m.state === "expiring" ? "Expiring" : "Blocking",
                  m.state === "expiring" ? "warn" : "bad",
                ),
                " ",
                m.text,
              ),
            ),
          )
        : el(
            "p",
            { class: "muted", style: "margin:0" },
            "Everything blocking is approved and current.",
          ),
      r.notes.length
        ? el(
            "details",
            { class: "small", style: "margin-top:8px" },
            el("summary", {}, `${r.notes.length} not blocking, still to collect`),
            el("ul", { class: "plainlist" }, ...r.notes.map((n) => el("li", {}, n.text))),
          )
        : null,
    );

    const docs = card(
      `Compliance for ${screen === "drivers" ? "Transportation" : "Home Care"}`,
      view.canEdit
        ? `Upload, approve, reject or waive. Blocking items stop ${p.firstName} from working.`
        : "What's on file.",
      [],
      docTable(view.documents, "Documents"),
    );
    const other = view.other.length
      ? card(
          "Other documents on file",
          "Kept current for other work",
          [],
          docTable(view.other, "Other documents"),
        )
      : null;

    const qualifies = card(
      "Qualifies for",
      "Clients and units with their own requirements, on top of the title's and the module's",
      [],
      view.qualifies.length
        ? table(
            [
              {
                h: "Client",
                f: (q) =>
                  el(
                    "span",
                    {},
                    el("b", {}, q.client),
                    q.unit ? el("div", { class: "small muted" }, q.unit) : null,
                  ),
              },
              { h: "Ready", f: (q) => readyChip(q.ready) },
              {
                h: "Still needs",
                f: (q) =>
                  q.missing.length
                    ? el("span", { class: "small" }, q.missing.join(", "))
                    : muted("Nothing, ready"),
              },
            ],
            view.qualifies,
            undefined,
            "Qualifies for",
          )
        : el(
            "p",
            { class: "muted" },
            "No client asks for more than the title and module yet. Set client requirements in Settings › Requirements library.",
          ),
    );

    const details = card(
      "Details",
      null,
      view.canEdit ? [ib("pencil", "Edit details", editDetails)] : [],
      el(
        "dl",
        { class: "kv" },
        el("dt", {}, "Does"),
        el("dd", {}, p.roleLabels.join(", ")),
        el("dt", {}, "Job"),
        el("dd", {}, view.jobs.find((j) => j.id === view.job)?.name ?? "–"),
        el("dt", {}, "Status"),
        el(
          "dd",
          {},
          p.statusLabel,
          p.statusReason ? el("div", { class: "small muted" }, p.statusReason) : null,
        ),
        el("dt", {}, "Mobile"),
        el("dd", {}, p.mobile ?? chip("No mobile", "warn")),
        el("dt", {}, "Email"),
        el("dd", {}, p.email ?? "–"),
        el("dt", {}, "Address"),
        el("dd", {}, p.addressLine ?? "No address yet"),
        el("dt", {}, "Map"),
        el("dd", { "data-map": p.map.status }, chip(p.map.label, p.map.level)),
      ),
    );
    const title = card(
      "Title and license",
      "Maryland Board of Nursing",
      view.canEdit ? [ib("pencil", "Edit title and license", editTitle)] : [],
      el(
        "dl",
        { class: "kv" },
        el("dt", {}, "Title"),
        el("dd", {}, pr.title ? `${pr.title} · ${pr.titleName ?? ""}` : "No title"),
        el("dt", {}, "License"),
        el(
          "dd",
          {},
          pr.licenseNumber ? `${pr.licenseState ?? ""} ${pr.licenseNumber}`.trim() : "–",
        ),
        el("dt", {}, "Specialties"),
        el("dd", {}, pr.specialties.join(", ") || "–"),
        el("dt", {}, "Languages"),
        el("dd", {}, pr.languages.join(", ") || "–"),
      ),
    );

    page.replaceChildren(
      head,
      ...(p.status !== "active"
        ? [
            el(
              "p",
              { class: "note", role: "status" },
              `${p.statusLabel}${p.statusReason ? `: ${p.statusReason}` : ""}`,
            ),
          ]
        : []),
      ...(screen === "caregivers" ? [bandSlot] : [boxes]),
      readiness,
      docs,
      ...(other ? [other] : []),
      qualifies,
      el("div", { class: "recgrid" }, details, title),
      availSlot,
      matchSlot,
      paySlot,
      ptoSlot,
      earnSlot,
      workedSlot,
    );
  }

  draw();
}
